import { useState } from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";

const ContactForm = ({ compact = false }: { compact?: boolean }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const t = useTranslation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      prenom: formData.get("prenom") as string,
      nom: formData.get("nom") as string,
      etablissement: formData.get("etablissement") as string,
      fonction: formData.get("fonction") as string,
      email: formData.get("email") as string,
      telephone: formData.get("telephone") as string,
      type_etablissement: formData.get("type") as string,
      etoiles: formData.get("etoiles") as string,
      localisation: formData.get("localisation") as string,
      message: formData.get("message") as string,
    };

    try {
      const { data: result, error } = await supabase.functions.invoke(
        "send-contact-email",
        { body: data }
      );

      if (error) throw error;
      
      // Google Ads conversion tracking
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'conversion', {
          'send_to': 'AW-18006017839/maN_CNTQk4YcEK-O-IlD',
          'value': 1.0,
          'currency': 'EUR',
        });
      }
      
      setSubmitted(true);
    } catch (err) {
      console.error("Erreur envoi:", err);
      toast.error(t.form.errorText);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <CheckCircle className="mx-auto text-gold mb-4" size={48} />
        <h3 className="font-display text-2xl text-cream mb-2">{t.form.successTitle}</h3>
        <p className="text-muted-foreground font-body">
          {t.form.successText} <strong className="text-gold">{t.form.successDelay}</strong>.
        </p>
      </motion.div>
    );
  }

  const inputClass =
    "w-full bg-secondary/50 border border-gold-subtle rounded-md px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground font-body focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className={`grid ${compact ? "grid-cols-1" : "md:grid-cols-2"} gap-4`}>
        <input required className={inputClass} placeholder={t.form.firstName} name="prenom" />
        <input required className={inputClass} placeholder={t.form.lastName} name="nom" />
      </div>
      <div className={`grid ${compact ? "grid-cols-1" : "md:grid-cols-2"} gap-4`}>
        <input required className={inputClass} placeholder={t.form.establishment} name="etablissement" />
        <input className={inputClass} placeholder={t.form.function} name="fonction" />
      </div>
      <div className={`grid ${compact ? "grid-cols-1" : "md:grid-cols-2"} gap-4`}>
        <input required type="email" className={inputClass} placeholder={t.form.email} name="email" />
        <input type="tel" className={inputClass} placeholder={t.form.phone} name="telephone" />
      </div>
      <div className={`grid ${compact ? "grid-cols-1" : "md:grid-cols-2"} gap-4`}>
        <div>
          <label htmlFor="contact-type" className="sr-only">{t.form.typeLabel}</label>
          <select required className={inputClass} name="type" id="contact-type" defaultValue="" aria-label={t.form.typeLabel}>
            <option value="" disabled>{t.form.typeLabel}</option>
            {t.form.types.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <input className={inputClass} placeholder={t.form.stars} name="etoiles" />
      </div>
      <input className={inputClass} placeholder={t.form.location} name="localisation" />
      <textarea
        required
        rows={4}
        className={inputClass + " resize-none"}
        placeholder={t.form.message}
        name="message"
      />
      <label className="flex items-start gap-3 cursor-pointer min-h-[48px] py-2">
        <input
          type="checkbox"
          required
          className="mt-1 accent-gold w-5 h-5 min-w-[20px]"
        />
        <span className="text-xs text-muted-foreground leading-relaxed">
          {t.form.consent}
        </span>
      </label>
      <Button type="submit" variant="gold" size="lg" className="w-full text-base" disabled={loading}>
        {loading ? t.form.sending : t.form.submit}
      </Button>
    </form>
  );
};

export default ContactForm;
